import { type FormEvent, type JSX, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/icons';
import {
  Badge,
  Button,
  Card,
  ConfirmModal,
  IconAction,
  Input,
  Skeleton,
  Spinner,
} from '@/components/ui';
import {
  useCreateProvider,
  useDeleteProvider,
  useProbeProvider,
  useProviderBalance,
  useProviderForEdit,
  useProviderReconcile,
  useProviders,
  useReactivateProvider,
  useSmokeTestProvider,
  useUpdateProvider,
  type Provider,
  type ProviderChannel,
  type ProviderHealthStatus,
  type ProviderProtocol,
  type ReconcileReport,
  type SmokeTestResult,
  type UpdateProviderInput,
} from '@/hooks/useAdminProviders';
import { useAuth } from '@/hooks/useAuth';
import { ApiError } from '@/lib/api';
import { formatRelativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Platform admin → Providers (carriers + their API credentials).
// Lives at /admin/providers, gated server-side by RequirePlatform.
// Resellers don't see the sidebar item (filter on tenantType).
//
// Credential inputs vary per carrier (Twilio: account_sid + auth_token;
// Bandwidth: account_id + api_token + api_secret + …); the form
// dynamically renders the right field set based on `name`.
// ---------------------------------------------------------------------------
export default function AdminProvidersPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const { user } = useAuth();
  const isPlatform = user?.tenantType === 'platform';
  const list = useProviders();
  const remove = useDeleteProvider();

  const [editingId, setEditingId] = useState<number | null>(null); // null = closed; -1 = "new"; >0 = editing existing
  const [pendingDelete, setPendingDelete] = useState<Provider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const items = list.data?.items ?? [];

  const handleDelete = async (): Promise<void> => {
    if (!pendingDelete) return;
    setError(null);
    try {
      await remove.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('apr_err_generic'));
      setPendingDelete(null);
    }
  };

  return (
    <div className="px-8 py-8 max-w-[1100px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('apr_title')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">
            {items.length === 0
              ? t('apr_sub_empty')
              : t('apr_sub', { total: items.length.toLocaleString(lang) })}
          </p>
        </div>
        <Button onClick={() => setEditingId(-1)}>
          <Icon.plus width="14" height="14" />
          {t('apr_new')}
        </Button>
      </header>

      {error && (
        <div role="alert" className="mb-4 flex items-start gap-3 px-4 py-3 bg-danger-bg border border-danger-bd rounded-md">
          <Icon.warn width="16" height="16" className="text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[13px] text-danger">{error}</div>
          <button type="button" onClick={() => setError(null)} className="text-[12px] text-danger/80 hover:text-danger">×</button>
        </div>
      )}

      <Card padding="none">
        {list.isLoading ? (
          <div className="p-5 space-y-2">
            {[0, 1, 2].map((i) => (<Skeleton key={i} className="h-12 w-full" />))}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-[14px] text-text-2 font-medium">{t('apr_empty_title')}</p>
            <p className="text-[13px] text-text-3 mt-1 max-w-[460px] mx-auto">{t('apr_empty_sub')}</p>
            <Button className="mt-5" onClick={() => setEditingId(-1)}>
              <Icon.plus width="14" height="14" />
              {t('apr_new')}
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_name')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_channel')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_health')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_secrets')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_active')}</th>
                  <th className="px-5 py-2.5 font-medium">{t('apr_col_last_check')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('apr_col_actions')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <ProviderRow
                    key={p.id}
                    provider={p}
                    lang={lang}
                    onEdit={() => setEditingId(p.id)}
                    onDelete={() => setPendingDelete(p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Billing reconciler — platform-only. Invisible to customers AND
          resellers: the endpoint is gated by RequirePlatform server-side and
          the panel is rendered only for the platform super-admin. */}
      {isPlatform && items.length > 0 && <ReconcilePanel providers={items} lang={lang} />}

      {editingId !== null && (
        <ProviderFormModal
          providerId={editingId === -1 ? null : editingId}
          onClose={() => setEditingId(null)}
        />
      )}

      <ConfirmModal
        open={pendingDelete !== null}
        title={t('apr_delete_title')}
        description={pendingDelete ? t('apr_delete_desc', { name: pendingDelete.name }) : ''}
        confirmLabel={t('apr_delete_action')}
        cancelLabel={t('apr_delete_cancel')}
        tone="danger"
        busy={remove.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setPendingDelete(null)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reconcile panel (platform-only)
// ---------------------------------------------------------------------------
// Pulls a carrier's per-message billing log for a bounded window (≤48h) and
// reconciles it against our records to answer: are we failing to charge for
// anything (margin leak), do delivery statuses match, and what is the
// carrier's real per-destination rate. Lazy: nothing runs until the operator
// clicks "Run reconciliation".

/** Formats an ISO string for a <input type="datetime-local"> value. */
function toLocalInput(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface ReconcilePanelProps {
  providers: Provider[];
  lang: string;
}
function ReconcilePanel({ providers, lang }: ReconcilePanelProps): JSX.Element {
  const { t } = useTranslation('default');
  const now = new Date();
  const dayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

  const [providerId, setProviderId] = useState<number>(providers[0]?.id ?? 0);
  const [since, setSince] = useState<string>(toLocalInput(dayAgo));
  const [until, setUntil] = useState<string>(toLocalInput(now));
  const [report, setReport] = useState<ReconcileReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reconcile = useProviderReconcile(providerId);

  const money = (n: number, currency: string): string =>
    `${n.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}${currency ? ` ${currency}` : ''}`;
  const rate = (n: number): string => n.toLocaleString(lang, { minimumFractionDigits: 4, maximumFractionDigits: 6 });

  const run = (): void => {
    setError(null);
    setReport(null);
    const sinceDate = new Date(since);
    const untilDate = new Date(until);
    if (Number.isNaN(sinceDate.getTime()) || Number.isNaN(untilDate.getTime()) || untilDate <= sinceDate) {
      setError(t('apr_rec_err_range'));
      return;
    }
    // 48h client-side guard (the server enforces the same bound).
    if (untilDate.getTime() - sinceDate.getTime() > 48 * 60 * 60 * 1000) {
      setError(t('apr_rec_err_window'));
      return;
    }
    reconcile.mutate(
      { since: sinceDate.toISOString(), until: untilDate.toISOString() },
      {
        onSuccess: (data) => setReport(data),
        onError: (err) => setError(err instanceof ApiError ? err.message : t('apr_err_generic')),
      },
    );
  };

  return (
    <Card className="mt-6" padding="lg">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="display text-[16px] font-semibold text-text m-0">{t('apr_rec_title')}</h2>
          <p className="text-[13px] text-text-3 mt-1 max-w-[640px]">{t('apr_rec_sub')}</p>
        </div>
      </div>

      <div className="flex items-end gap-3 flex-wrap mt-4">
        <label className="flex flex-col gap-1 text-[12px] text-text-3">
          {t('apr_rec_provider')}
          <select
            className="h-9 rounded-md border border-line bg-surface px-2 text-[13px] text-text"
            value={providerId}
            onChange={(e) => setProviderId(Number(e.target.value))}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-text-3">
          {t('apr_rec_since')}
          <input
            type="datetime-local"
            className="h-9 rounded-md border border-line bg-surface px-2 text-[13px] text-text"
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[12px] text-text-3">
          {t('apr_rec_until')}
          <input
            type="datetime-local"
            className="h-9 rounded-md border border-line bg-surface px-2 text-[13px] text-text"
            value={until}
            onChange={(e) => setUntil(e.target.value)}
          />
        </label>
        <Button onClick={run} disabled={reconcile.isPending}>
          {reconcile.isPending ? <Spinner /> : <Icon.chart width="14" height="14" />}
          {reconcile.isPending ? t('apr_rec_running') : t('apr_rec_run')}
        </Button>
      </div>

      {error && (
        <div role="alert" className="mt-4 flex items-start gap-3 px-4 py-3 bg-danger-bg border border-danger-bd rounded-md">
          <Icon.warn width="16" height="16" className="text-danger flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-[13px] text-danger">{error}</div>
        </div>
      )}

      {report && (
        <div className="mt-5">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label={t('apr_rec_matched')} value={report.matched_ok.toLocaleString(lang)} />
            <SummaryCard
              label={t('apr_rec_underbilled')}
              value={report.under_billed.toLocaleString(lang)}
              detail={money(report.under_billed_carrier_cost, report.currency)}
              tone={report.under_billed > 0 ? 'danger' : 'neutral'}
            />
            <SummaryCard
              label={t('apr_rec_orphans')}
              value={report.orphans.toLocaleString(lang)}
              tone={report.orphans > 0 ? 'warn' : 'neutral'}
            />
            <SummaryCard
              label={t('apr_rec_drift')}
              value={report.status_drifts.toLocaleString(lang)}
              tone={report.status_drifts > 0 ? 'warn' : 'neutral'}
            />
          </div>

          <p className="text-[12px] text-text-4 mt-3">
            {t('apr_rec_summary', {
              priced: report.priced_lines.toLocaleString(lang),
              expected: money(report.under_billed_expected_sell, report.currency),
            })}
          </p>

          {/* Per-destination rate table */}
          {report.rate_table.length > 0 && (
            <div className="mt-5">
              <h3 className="text-[13px] font-semibold text-text mb-2">{t('apr_rec_rates_title')}</h3>
              <div className="overflow-x-auto border border-line rounded-md">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_mccmnc')}</th>
                      <th className="px-4 py-2 font-medium text-right">{t('apr_rec_col_lines')}</th>
                      <th className="px-4 py-2 font-medium text-right">{t('apr_rec_col_their_rate')}</th>
                      <th className="px-4 py-2 font-medium text-right">{t('apr_rec_col_our_cost')}</th>
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_flag')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rate_table.map((d) => (
                      <tr key={d.mcc_mnc || 'unknown'} className="border-b border-line/60 last:border-0">
                        <td className="px-4 py-2 font-mono text-[12px]">{d.mcc_mnc || '—'}</td>
                        <td className="px-4 py-2 text-right">{d.lines.toLocaleString(lang)}</td>
                        <td className="px-4 py-2 text-right font-mono text-[12px]">{rate(d.their_avg_rate)}</td>
                        <td className="px-4 py-2 text-right font-mono text-[12px]">
                          {d.our_cost_resolved ? rate(d.our_avg_cost) : '—'}
                        </td>
                        <td className="px-4 py-2">
                          {d.rate_mismatch ? (
                            <Badge variant="warn">{t('apr_rec_mismatch')}</Badge>
                          ) : d.our_cost_resolved ? (
                            <Badge variant="success">{t('apr_rec_ok')}</Badge>
                          ) : (
                            <span className="text-text-4 text-[12px]">{t('apr_rec_unresolved')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Problem rows */}
          {report.problem_rows.length > 0 && (
            <div className="mt-5">
              <h3 className="text-[13px] font-semibold text-text mb-2">
                {t('apr_rec_problems_title', { shown: report.problem_rows.length, total: report.problems_total })}
              </h3>
              <div className="overflow-x-auto border border-line rounded-md">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_msgid')}</th>
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_class')}</th>
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_ourstatus')}</th>
                      <th className="px-4 py-2 font-medium">{t('apr_rec_col_theirstatus')}</th>
                      <th className="px-4 py-2 font-medium text-right">{t('apr_rec_col_theirprice')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.problem_rows.map((p) => (
                      <tr key={p.message_id} className="border-b border-line/60 last:border-0">
                        <td className="px-4 py-2 font-mono text-[11px]">{p.message_id}</td>
                        <td className="px-4 py-2">
                          {p.classification === 'under_billed' ? (
                            <Badge variant="danger">{t('apr_rec_class_underbilled')}</Badge>
                          ) : p.classification === 'orphan' ? (
                            <Badge variant="warn">{t('apr_rec_class_orphan')}</Badge>
                          ) : (
                            <Badge variant="neutral">{t('apr_rec_class_matched')}</Badge>
                          )}
                          {p.status_drift && <Badge variant="warn" className="ml-1">{t('apr_rec_class_drift')}</Badge>}
                        </td>
                        <td className="px-4 py-2">{p.our_status || '—'}</td>
                        <td className="px-4 py-2">{p.their_status || '—'}</td>
                        <td className="px-4 py-2 text-right font-mono text-[12px]">{rate(p.their_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  detail?: string;
  tone?: 'neutral' | 'danger' | 'warn';
}
function SummaryCard({ label, value, detail, tone = 'neutral' }: SummaryCardProps): JSX.Element {
  const toneClass =
    tone === 'danger'
      ? 'border-danger-bd bg-danger-bg'
      : tone === 'warn'
        ? 'border-warn-bd bg-warn-bg'
        : 'border-line bg-surface-2';
  const valueClass = tone === 'danger' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-text';
  return (
    <div className={cn('rounded-md border px-4 py-3', toneClass)}>
      <div className="text-[11px] uppercase tracking-[0.06em] text-text-4">{label}</div>
      <div className={cn('text-[22px] font-semibold mt-0.5', valueClass)}>{value}</div>
      {detail && <div className="text-[12px] text-text-3 mt-0.5">{detail}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
interface ProviderRowProps {
  provider: Provider;
  lang: string;
  onEdit: () => void;
  onDelete: () => void;
}
function ProviderRow({ provider, lang, onEdit, onDelete }: ProviderRowProps): JSX.Element {
  const { t } = useTranslation('default');
  const { user } = useAuth();
  // Wholesale balance is a platform-only signal (OUR carrier credit). It
  // must be invisible to customers AND resellers, so we render its trigger
  // + readout only for the platform super-admin. The endpoint is also
  // gated server-side by RequirePlatform — this is defense in depth.
  const isPlatform = user?.tenantType === 'platform';
  const probe = useProbeProvider();
  const reactivate = useReactivateProvider();
  const balance = useProviderBalance(provider.id);
  const [probeMsg, setProbeMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [smokeOpen, setSmokeOpen] = useState(false);

  // A provider is "auto-disabled" when the worker pulled it out (has a
  // last_auto_disabled_at timestamp) AND is currently inactive. Once the
  // operator reactivates we keep the audit fields but hide the prominent
  // banner — the disable history is still visible in the edit form.
  const isAutoDisabled = !provider.is_active && Boolean(provider.last_auto_disabled_at);

  const handleProbe = async (): Promise<void> => {
    setProbeMsg(null);
    try {
      const res = await probe.mutateAsync(provider.id);
      setProbeMsg({
        ok: res.ok,
        text: res.ok
          ? t('apr_probe_ok', { ms: res.latency_ms })
          : t('apr_probe_fail', { error: res.error ?? '' }),
      });
    } catch (err) {
      setProbeMsg({ ok: false, text: err instanceof ApiError ? err.message : t('apr_err_generic') });
    }
  };

  const handleReactivate = async (): Promise<void> => {
    try {
      await reactivate.mutateAsync(provider.id);
    } catch {
      // Refetch surfaces the failure inline; nothing to do here.
    }
  };

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2 align-top">
      <td className="px-5 py-3">
        <div className="text-text font-medium">{provider.name}</div>
        <div className="text-[11px] text-text-4 mt-0.5">{provider.protocol}</div>
      </td>
      <td className="px-5 py-3 text-text-2">
        <span className="mono text-[12px]">{provider.channel}</span>
      </td>
      <td className="px-5 py-3">
        <HealthBadge status={provider.health_status} failures={provider.consecutive_failures} />
        {probeMsg && (
          <div className={cn('text-[11px] mt-1', probeMsg.ok ? 'text-success' : 'text-danger')}>
            {probeMsg.text}
          </div>
        )}
        {isPlatform && (balance.isFetching || balance.isError || balance.data) && (
          <div className="text-[11px] mt-1">
            {balance.isFetching ? (
              <span className="text-text-4">{t('apr_balance_checking')}</span>
            ) : balance.isError ? (
              <span className="text-danger">
                {balance.error instanceof ApiError ? balance.error.message : t('apr_err_generic')}
              </span>
            ) : balance.data ? (
              <span className="text-text-2">
                {t('apr_balance_value', {
                  amount: `${balance.data.balance.toLocaleString(lang, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })} ${balance.data.currency}`,
                })}
                <span className="text-text-4">
                  {' · '}
                  {formatRelativeTime(balance.data.checked_at, lang)}
                </span>
              </span>
            ) : null}
          </div>
        )}
      </td>
      <td className="px-5 py-3">
        {provider.secrets_configured ? (
          <Badge variant="success">{t('apr_secrets_yes')}</Badge>
        ) : (
          <Badge variant="warn">{t('apr_secrets_no')}</Badge>
        )}
      </td>
      <td className="px-5 py-3">
        {isAutoDisabled ? (
          <div>
            <Badge variant="danger">{t('apr_auto_disabled')}</Badge>
            {provider.last_auto_disable_reason && (
              <div className="text-[11px] text-text-3 mt-1 max-w-[260px] leading-snug">
                {provider.last_auto_disable_reason}
              </div>
            )}
            {provider.last_auto_disabled_at && (
              <div className="text-[11px] text-text-4 mt-0.5">
                {formatRelativeTime(provider.last_auto_disabled_at, lang)}
              </div>
            )}
          </div>
        ) : provider.is_active ? (
          <Badge variant="success">{t('apr_active_yes')}</Badge>
        ) : (
          <Badge variant="neutral">{t('apr_active_no')}</Badge>
        )}
      </td>
      <td className="px-5 py-3 text-text-3 text-[12px] whitespace-nowrap">
        {provider.last_health_check_at ? formatRelativeTime(provider.last_health_check_at, lang) : '—'}
      </td>
      <td className="px-5 py-3 text-right">
        <div className="inline-flex gap-0.5">
          {isAutoDisabled && (
            <IconAction
              icon={Icon.play}
              label={t('apr_reactivate')}
              onClick={() => void handleReactivate()}
              disabled={reactivate.isPending}
              tone="primary"
            />
          )}
          {isPlatform && (
            <IconAction
              icon={Icon.chart}
              label={t('apr_balance')}
              onClick={() => void balance.refetch()}
              disabled={balance.isFetching}
            />
          )}
          <IconAction
            icon={Icon.bell}
            label={t('apr_probe')}
            onClick={() => void handleProbe()}
            disabled={probe.isPending}
          />
          <IconAction
            icon={Icon.campaigns}
            label={t('apr_smoke')}
            onClick={() => setSmokeOpen(true)}
            tone="primary"
          />
          <IconAction icon={Icon.pencil} label={t('apr_edit')} onClick={onEdit} />
          <IconAction icon={Icon.trash} label={t('apr_delete')} onClick={onDelete} tone="danger" />
        </div>
      </td>
      {smokeOpen && (
        <SmokeTestModal
          provider={provider}
          onClose={() => setSmokeOpen(false)}
        />
      )}
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Smoke test modal — fires a real send through the carrier and surfaces
// the result inline. Costs the operator real carrier credit; the
// warning banner makes that explicit before submit. The /v1/admin/
// providers/{id}/smoke-test handler validates from/to/body and runs the
// per-carrier SendSMS in HTTPSmokeTester (api/internal/services/
// provider_smoketest.go).
// ---------------------------------------------------------------------------
interface SmokeTestModalProps {
  provider: Provider;
  onClose: () => void;
}
function SmokeTestModal({ provider, onClose }: SmokeTestModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const smoke = useSmokeTestProvider();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [body, setBody] = useState('Test from EzSender');
  const [result, setResult] = useState<SmokeTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setResult(null);
    try {
      const res = await smoke.mutateAsync({
        id: provider.id,
        input: { from: from.trim() || undefined, to: to.trim(), body },
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('apr_err_generic'));
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {t('apr_smoke_title', { name: provider.name })}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('apr_smoke_sub')}</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="px-3 py-2 bg-warn-bg border border-warn-bd rounded-md text-[12px] text-warn">
            <strong className="font-semibold">{t('apr_smoke_warning_title')}</strong>{' '}
            {t('apr_smoke_warning_body')}
          </div>

          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">
              {error}
            </div>
          )}

          <Field label={t('apr_smoke_from')} hint={t('apr_smoke_from_hint')}>
            <Input
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              placeholder="+15551234567"
            />
          </Field>
          <Field label={t('apr_smoke_to')} hint={t('apr_smoke_to_hint')}>
            <Input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="+15558675309"
              required
            />
          </Field>
          <Field label={t('apr_smoke_body')}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={160}
              required
              rows={3}
              className="w-full px-3 py-2 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow resize-none"
            />
          </Field>

          {result && <SmokeTestResultPanel result={result} />}
        </div>

        <div className="px-5 py-3 border-t border-line flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('apr_smoke_close')}
          </Button>
          <Button type="submit" disabled={smoke.isPending || !to.trim() || !body.trim()}>
            {smoke.isPending ? t('apr_smoke_sending') : t('apr_smoke_send')}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function SmokeTestResultPanel({ result }: { result: SmokeTestResult }): JSX.Element {
  const { t } = useTranslation('default');
  if (result.ok) {
    return (
      <div className="px-3 py-2 bg-success-bg border border-success-bd rounded-md text-[12px] space-y-1">
        <div className="font-semibold text-success">
          {t('apr_smoke_ok_title', { ms: result.latency_ms })}
        </div>
        {result.provider_msg_id && (
          <div className="text-text-2">
            <span className="text-text-3">{t('apr_smoke_msg_id')}:</span>{' '}
            <span className="mono">{result.provider_msg_id}</span>
          </div>
        )}
        <div className="text-text-3 mono text-[11px] truncate" title={result.request_summary}>
          {result.request_summary}
        </div>
      </div>
    );
  }
  return (
    <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[12px] space-y-1">
      <div className="font-semibold text-danger">
        {t('apr_smoke_fail_title', { code: result.error_code || `HTTP ${result.http_status}` })}
      </div>
      {result.error_message && (
        <div className="text-text-2 break-words">{result.error_message}</div>
      )}
      <div className="text-text-3 mono text-[11px] truncate" title={result.request_summary}>
        {result.request_summary} · {result.latency_ms}ms
      </div>
    </div>
  );
}

interface HealthBadgeProps {
  status: ProviderHealthStatus;
  failures: number;
}
function HealthBadge({ status, failures }: HealthBadgeProps): JSX.Element {
  const { t } = useTranslation('default');
  switch (status) {
    case 'up':
      return <Badge variant="success">{t('apr_health_up')}</Badge>;
    case 'down':
      return <Badge variant="danger">{t('apr_health_down', { failures })}</Badge>;
    default:
      return <Badge variant="neutral">{t('apr_health_unknown')}</Badge>;
  }
}

// ---------------------------------------------------------------------------
// Form modal — used for both create and edit. When editing, fetches
// the provider with decrypted secrets pre-filled.
// ---------------------------------------------------------------------------
interface ProviderFormModalProps {
  providerId: number | null; // null = create mode
  onClose: () => void;
}
function ProviderFormModal({ providerId, onClose }: ProviderFormModalProps): JSX.Element {
  const { t } = useTranslation('default');
  const isEdit = providerId !== null;
  const create = useCreateProvider();
  const update = useUpdateProvider();
  const editQuery = useProviderForEdit(providerId);

  // Render a loading state until the edit fetch resolves; create mode
  // renders immediately with an empty form.
  if (isEdit && editQuery.isLoading) {
    return (
      <ModalShell onClose={onClose}>
        <div className="px-5 py-10 text-center text-[13px] text-text-3">{t('apr_loading')}</div>
      </ModalShell>
    );
  }
  if (isEdit && editQuery.isError) {
    return (
      <ModalShell onClose={onClose}>
        <div className="px-5 py-10 text-center text-[13px] text-danger">{t('apr_load_failed')}</div>
      </ModalShell>
    );
  }

  return (
    <ProviderForm
      existing={isEdit ? editQuery.data ?? null : null}
      submitting={create.isPending || update.isPending}
      onClose={onClose}
      onSubmit={async (input) => {
        if (isEdit && providerId !== null) {
          const updateInput: UpdateProviderInput = {
            protocol: input.protocol,
            channel: input.channel,
            config: input.config,
            is_active: input.is_active,
            refund_on_failure: input.refund_on_failure,
            // Only send secrets when the operator changed them — if all
            // values match the originally-loaded ones, leave undefined
            // so the backend keeps the existing encrypted blob.
            secrets: input.secretsChanged ? input.secrets : undefined,
          };
          await update.mutateAsync({ id: providerId, input: updateInput });
        } else {
          await create.mutateAsync({
            name: input.name,
            protocol: input.protocol,
            channel: input.channel,
            config: input.config,
            secrets: input.secrets,
            is_active: input.is_active,
            refund_on_failure: input.refund_on_failure,
          });
        }
        onClose();
      }}
    />
  );
}

interface FormSubmit {
  name: string;
  protocol: ProviderProtocol;
  channel: ProviderChannel;
  config: Record<string, unknown>;
  secrets: Record<string, string>;
  secretsChanged: boolean;
  is_active: boolean;
  refund_on_failure: boolean;
}

interface ProviderFormProps {
  existing: { name: string; protocol: ProviderProtocol; channel: ProviderChannel; config: Record<string, unknown>; secrets: Record<string, string>; is_active: boolean; refund_on_failure: boolean } | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (input: FormSubmit) => Promise<void>;
}
function ProviderForm({ existing, submitting, onClose, onSubmit }: ProviderFormProps): JSX.Element {
  const { t } = useTranslation('default');
  const [name, setName] = useState(existing?.name ?? '');
  const [protocol, setProtocol] = useState<ProviderProtocol>(existing?.protocol ?? 'http_rest');
  const [channel, setChannel] = useState<ProviderChannel>(existing?.channel ?? 'sms');
  const [baseURL, setBaseURL] = useState(((existing?.config?.['base_url'] as string) ?? ''));
  const [isActive, setIsActive] = useState(existing?.is_active ?? true);
  // Default ON — every existing provider refunds today; an operator opts
  // a carrier out (e.g. Infobip) only deliberately. See migration 000114.
  const [refundOnFailure, setRefundOnFailure] = useState(existing?.refund_on_failure ?? true);
  const [secrets, setSecrets] = useState<Record<string, string>>(existing?.secrets ?? {});
  // Per-field reveal so an operator can read + copy a masked secret — e.g. to
  // paste the Infobip webhook_secret into the DLR/inbound callback URL.
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const initialSecretsKey = JSON.stringify(existing?.secrets ?? {});
  const [error, setError] = useState<string | null>(null);

  // Keep the base_url in sync if the form remounts with new existing data
  // (defensive — typically the modal closes between edits so this rarely fires).
  useEffect(() => {
    if (existing) {
      setBaseURL(((existing.config?.['base_url'] as string) ?? ''));
    }
  }, [existing]);

  const fields = secretFieldsFor(name || existing?.name || '');
  const secretsChanged = JSON.stringify(secrets) !== initialSecretsKey;

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!existing && name.trim() === '') {
      setError(t('apr_err_name'));
      return;
    }
    try {
      await onSubmit({
        name: existing?.name ?? name.trim(),
        protocol,
        channel,
        config: { base_url: baseURL.trim() },
        secrets,
        secretsChanged,
        is_active: isActive,
        refund_on_failure: refundOnFailure,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : t('apr_err_generic'));
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <form onSubmit={(e) => void submit(e)}>
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">
            {existing ? t('apr_form_edit_title', { name: existing.name }) : t('apr_form_new_title')}
          </h2>
          <p className="text-[12px] text-text-3 mt-0.5">{t('apr_form_sub')}</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {error && (
            <div className="px-3 py-2 bg-danger-bg border border-danger-bd rounded-md text-[13px] text-danger">{error}</div>
          )}

          <Field label={t('apr_field_name')} hint={existing ? t('apr_field_name_locked') : t('apr_field_name_hint')}>
            <Input
              value={existing?.name ?? name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder=""
              maxLength={100}
              disabled={existing !== null}
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('apr_field_protocol')}>
              <select
                value={protocol}
                onChange={(e) => setProtocol(e.target.value as ProviderProtocol)}
                className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
              >
                <option value="http_rest">http_rest</option>
                <option value="smpp">smpp</option>
                <option value="sip">sip</option>
              </select>
            </Field>
            <Field label={t('apr_field_channel')}>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as ProviderChannel)}
                className="w-full h-9 px-3 text-[13px] rounded-md bg-surface border border-line text-text outline-none focus:border-volt-500 focus:ring-[3px] focus:ring-volt-glow"
              >
                <option value="sms">sms</option>
                <option value="voice">voice</option>
                <option value="both">both</option>
              </select>
            </Field>
          </div>

          <Field label={t('apr_field_base_url')}>
            <Input
              value={baseURL}
              onChange={(e) => setBaseURL(e.target.value)}
              placeholder="https://api.example.com"
            />
          </Field>

          <label className="inline-flex items-center gap-2 text-[13px] text-text">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            {t('apr_field_active')}
          </label>

          <div>
            <label className="inline-flex items-center gap-2 text-[13px] text-text">
              <input
                type="checkbox"
                checked={refundOnFailure}
                onChange={(e) => setRefundOnFailure(e.target.checked)}
              />
              {t('apr_field_refund_on_failure')}
            </label>
            <p className="text-[11px] text-text-4 mt-1">{t('apr_field_refund_on_failure_hint')}</p>
          </div>

          {fields.length > 0 && (
            <div className="pt-2 border-t border-line-soft space-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.06em] text-text-4 font-medium">
                  {t('apr_secrets_section')}
                </div>
                <p className="text-[12px] text-text-3 mt-1">
                  {existing ? t('apr_secrets_help_edit') : t('apr_secrets_help_create')}
                </p>
              </div>
              {fields.map((f) => (
                <Field key={f.key} label={f.label} hint={f.hint}>
                  <div className="flex items-center gap-2">
                    <Input
                      className="flex-1"
                      type={f.sensitive && !revealed[f.key] ? 'password' : 'text'}
                      value={secrets[f.key] ?? ''}
                      onChange={(e) => setSecrets((s) => ({ ...s, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                    />
                    {f.sensitive && (
                      <button
                        type="button"
                        onClick={() => setRevealed((r) => ({ ...r, [f.key]: !r[f.key] }))}
                        className="text-[12px] font-medium text-volt-ink hover:underline whitespace-nowrap px-1"
                      >
                        {revealed[f.key] ? 'Hide' : 'Show'}
                      </button>
                    )}
                  </div>
                </Field>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-line flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            {t('apr_form_cancel')}
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner />
                <span>{t('apr_form_saving')}</span>
              </>
            ) : existing ? (
              t('apr_form_save')
            ) : (
              t('apr_form_create')
            )}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ children, onClose }: { children: React.ReactNode; onClose: () => void }): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[560px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: JSX.Element }): JSX.Element {
  return (
    <div>
      <label className="block text-[12px] font-medium text-text-2 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-text-4 mt-1">{hint}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-carrier secret field definitions. The keys MUST match what the
// `carrierfactory` package looks up in the workers — if they drift, the
// admin would save a key under one name and the workers would never
// find it. Keep this in sync with workers/internal/carrierfactory/factory.go.
// ---------------------------------------------------------------------------
interface SecretFieldDef {
  key: string;
  label: string;
  placeholder?: string;
  hint?: string;
  sensitive: boolean;
}

function secretFieldsFor(providerName: string): SecretFieldDef[] {
  switch (providerName) {
    case 'twilio':
      return [
        { key: 'account_sid', label: 'Account SID', sensitive: false },
        { key: 'auth_token', label: 'Auth token', sensitive: true },
        { key: 'from_number', label: 'Default From number', sensitive: false, hint: 'E.164 format' },
        { key: 'status_callback', label: 'Status callback URL', sensitive: false, hint: 'Public URL the provider POSTs delivery receipts to' },
      ];
    case 'bandwidth':
      return [
        { key: 'account_id', label: 'Account ID', sensitive: false },
        { key: 'api_token', label: 'API token', sensitive: true },
        { key: 'api_secret', label: 'API secret', sensitive: true },
        { key: 'application_id', label: 'Application ID', sensitive: false },
        { key: 'from_number', label: 'Default From number', sensitive: false, hint: 'E.164 format' },
        { key: 'webhook_username', label: 'Webhook username', sensitive: true },
        { key: 'webhook_password', label: 'Webhook password', sensitive: true },
      ];
    case 'smsgateway':
      // smsgatewayco.com v3 API. Only api_token is strictly required;
      // sender_id is a per-tenant default used when the message itself
      // doesn't carry one.
      return [
        { key: 'api_token', label: 'API token', sensitive: true, hint: 'Bearer token from the smsgateway portal (e.g. "20|abc...")' },
        { key: 'sender_id', label: 'Default sender_id', sensitive: false, hint: 'Phone number (E.164 incl. country code) or alphanumeric ≤ 11 chars' },
      ];
    case 'infobip':
      // Auth is a single API key (Authorization: App <key>). webhook_secret
      // is the shared secret appended as ?secret= to the DLR + inbound
      // callback URLs so we can authenticate Infobip's callbacks to us.
      return [
        { key: 'api_key', label: 'API key', sensitive: true, hint: 'Sent as Authorization: App <key>' },
        {
          key: 'webhook_secret',
          label: 'Webhook secret',
          sensitive: true,
          hint: 'Append as ?secret=<value> to the DLR + inbound callback URLs in Infobip',
        },
      ];
    case 'mock':
      // Mock has no credentials — public base_url only.
      return [];
    default:
      // Unknown provider — show a generic empty form. The operator can
      // still save metadata; the worker will skip the row at boot
      // until an adapter is added in the carrierfactory.
      return [];
  }
}
